"use client";

import { cmpOptimizelyBridge } from "@/helpers/cmpOptimizelyBridge";
import { getOptimizelyExperimentData } from "@/helpers/getOptimizelyExperimentData";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect } from "react";

declare global {
  interface Window {
    optimizely?: {
      push: (args: unknown) => void;
      get?: (key: string) => unknown;
    };
    runOptimizelyBodySafeguard?: () => void;
    optimizelyShowBody?: () => void;
    setBuilderVariation?: (experimentKey: string, variationKey: string) => void;
    OnetrustActiveGroups?: string;
    __OPTLY_LISTENER_ATTACHED__?: boolean;
    __OPTLY_CLEANUP_LISTENER_ATTACHED__?: boolean;
    __OPTLY_BLOCK_AUTO_REVEAL__?: boolean;
    __IS_MOBILE__?: boolean;
    __OPTIMIZELY_DISABLED__?: boolean;
    __OPTIMIZELY_GUARD_RAN__?: boolean;
  }
}

const getClientCookieValue = (cookieName: string): string | null => {
  const cookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${cookieName}=`));
  return cookie
    ? decodeURIComponent(cookie.split("=").slice(1).join("="))
    : null;
};

const setBuilderVariationCookie = (
  experimentKey: string,
  variationKey: string,
  onCookieChanged = () => {},
  onNoChange = () => {},
) => {
  const cookieValue = variationKey;
  const cookieName = `builder.optimizelyVariation.${experimentKey}`;

  // Get existing cookie value
  const existingValue = getClientCookieValue(cookieName);

  // Only proceed if the value changed
  if (existingValue !== cookieValue) {
    const isSecure = window.location.protocol === "https:";
    const sameSiteAttr = isSecure ? "SameSite=None; Secure" : "SameSite=Lax";
    document.cookie = `${cookieName}=${encodeURIComponent(cookieValue)}; path=/; ${sameSiteAttr}`;

    // Verify cookie was actually persisted before triggering a reload.
    // If the browser blocks it (e.g. third-party iframe with strict settings)
    // we skip the reload to avoid an infinite reload loop.
    if (getClientCookieValue(cookieName) === cookieValue) {
      onCookieChanged();
    }
  } else {
    onNoChange();
  }
};

export default function OptimizelyTracker({
  enabled = true,
}: {
  enabled?: boolean;
}): null {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (enabled && !window.__OPTIMIZELY_DISABLED__) {
      window.setBuilderVariation = (
        experimentKey: string,
        variationKey: string,
      ) => {
        setBuilderVariationCookie(experimentKey, variationKey, () => {
          router.refresh();
        });
      };
    } else {
      window.setBuilderVariation = undefined;
    }

    return () => {
      window.setBuilderVariation = undefined;
    };
  }, [router, enabled]);

  useEffect(() => {
    if (!enabled || window.__OPTIMIZELY_DISABLED__) {
      return;
    }

    let isMounted = true;
    let timeoutId: number | null = null;
    let pagesActivated = false;
    let listenerAdded = false;

    // Trigger the body safeguard and showBody on mount (including on HMR rebuilds)
    const needsShow =
      document.body?.style.opacity !== "1" ||
      document.body?.style.visibility !== "visible";

    if (needsShow && typeof window.optimizelyShowBody === "function") {
      window.optimizelyShowBody();
    }
    if (needsShow && typeof window.runOptimizelyBodySafeguard === "function") {
      window.runOptimizelyBodySafeguard();
    }

    const activateAllPages = (): void => {
      if (!isMounted || pagesActivated) return;

      const optly = window.optimizely;
      if (!optly || typeof optly.get !== "function") {
        return;
      }

      const data = optly.get("data") as Record<string, unknown> | null;
      if (!data || typeof data !== "object" || !("pages" in data)) {
        return;
      }

      const pages = (data as { pages: Record<string, { apiName: string }> })
        .pages;
      Object.values(pages).forEach((p) => {
        optly.push({ type: "page", pageName: p.apiName, isActive: false });
        optly.push({ type: "page", pageName: p.apiName });
      });

      pagesActivated = true;
    };

    const handleInitialized = (): void => {
      if (!isMounted) return;
      activateAllPages();
    };

    let retryCount = 0;
    const maxRetries = 100;

    const waitUntilReady = (): void => {
      if (!isMounted) return;

      const optly = window.optimizely;
      if (optly && typeof optly.get === "function" && optly.get("data")) {
        activateAllPages();
      } else {
        if (retryCount >= maxRetries) {
          return;
        }
        retryCount += 1;
        timeoutId = window.setTimeout(waitUntilReady, 30);
      }
    };

    if (window.optimizely && typeof window.optimizely.push === "function") {
      window.optimizely.push({
        type: "addListener",
        filter: { type: "lifecycle", name: "initialized" },
        handler: handleInitialized,
      });
      listenerAdded = true;
    }

    waitUntilReady();

    return () => {
      isMounted = false;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      if (
        listenerAdded &&
        window.optimizely &&
        typeof window.optimizely.push === "function"
      ) {
        window.optimizely.push({
          type: "removeListener",
          filter: { type: "lifecycle", name: "initialized" },
          handler: handleInitialized,
        });
      }
    };
  }, [enabled]);

  useEffect(() => {
    if (window.__OPTIMIZELY_DISABLED__) {
      return;
    }

    const callGetExperimentData = () => {
      requestAnimationFrame(() => {
        // On non-opt-in pages we still run the CMP consent bridge for journey
        // tracking, but skip experiment activation (which pushes `activate`).
        if (enabled) {
          getOptimizelyExperimentData();
        }
        cmpOptimizelyBridge();
      });
    };

    callGetExperimentData();
  }, [pathname, searchParams, enabled]);

  return null;
}
